import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Input, Button, Row, Col, message, Spin, Modal } from "antd";
import Heading from "../../components/Heading";
import useCustomNavigate from "../../components/customNavigationHook";
import API from "../../api";
import "./codCenterOnboarding.scss";
import urls from "../../api/urls";
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxPopover,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";

const { DELIVERY_PARTNER_LIST, CREATE_COD_CENTER } = urls;

// API Usage Optimization Notes:
// 1. Reverse geocoding: 500ms debounce + 1s rate limiting + 3 retries with exponential backoff
// 2. Places search: 1000ms debounce + 2 retries with exponential backoff  
// 3. Caching: 50-item LRU cache for reverse geocoding results
// 4. Error handling: Specific messages for OVER_QUERY_LIMIT and REQUEST_DENIED

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// COD Center creation API URL
const CODCenterOnboarding = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deliveryPartnerData, setDeliveryPartnerData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const navigate = useCustomNavigate();
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState({
    lat: 18.5204, // Default to Pune
    lng: 73.8567,
    address: "",
  });
  const [locationSelected, setLocationSelected] = useState(false); // Track if location was properly selected
  const [gettingLocation, setGettingLocation] = useState(false); // Track geolocation loading state
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false); // Track if Google Maps is loaded

  // Manual Places Autocomplete state
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsStatus, setSuggestionsStatus] = useState("");
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);

  // Cache for reverse geocoding to avoid duplicate API calls
  const geocodeCache = useRef(new Map());
  const lastReverseGeocodeTime = useRef(0);
  const REVERSE_GEOCODE_DELAY = 1000; // 1 second delay between reverse geocode calls

  const mapRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Function to get user email from stored user info
  const getUserEmail = useCallback(() => {
    const userInfo = JSON.parse(localStorage.getItem("userinfo"));
    return userInfo?.username || "";
  }, []);

  // Function to update location and form fields
  const updateLocation = useCallback(
    (location, place = null) => {
      setSelectedLocation(location);
      setLocationSelected(true); // Mark that a location has been selected

      if (marker) {
        marker.setPosition(location);
      }

      if (map) {
        map.setCenter(location);
      }

      // Update form fields
      if (place) {
        const addressComponents = place.address_components || [];
        let fullAddress = place.formatted_address || "";
        let city = "";
        let state = "";
        let pincode = "";

        addressComponents.forEach((component) => {
          const types = component.types;
          if (
            types.includes("locality") ||
            types.includes("administrative_area_level_2")
          ) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (types.includes("postal_code")) {
            pincode = component.long_name;
          }
        });

        form.setFieldsValue({
          address: fullAddress,
          city: city,
          state: state,
          pincode: pincode,
          latitude: location.lat,
          longitude: location.lng,
        });
      } else {
        form.setFieldsValue({
          latitude: location.lat,
          longitude: location.lng,
        });
      }
    },
    [marker, map, form]
  );

  // Define optimized reverseGeocode function with caching and rate limiting
  const reverseGeocode = useCallback(
    debounce((location) => {
      // Rate limiting: prevent calls too close together
      const now = Date.now();
      if (now - lastReverseGeocodeTime.current < REVERSE_GEOCODE_DELAY) {
        return;
      }

      // Create cache key from coordinates (rounded to avoid too many cache entries)
      const cacheKey = `${location.lat.toFixed(4)},${location.lng.toFixed(4)}`;

      // Check cache first
      if (geocodeCache.current.has(cacheKey)) {
        const cachedResult = geocodeCache.current.get(cacheKey);
        updateLocation(
          { ...location, address: cachedResult.formatted_address },
          cachedResult
        );
        return;
      }

      lastReverseGeocodeTime.current = now;

      const geocoder = new window.google.maps.Geocoder();
      
      // Retry function with exponential backoff
      const attemptGeocode = (retryCount = 0) => {
        geocoder.geocode({ location }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];

            // Cache the result
            geocodeCache.current.set(cacheKey, place);

            // Limit cache size to prevent memory issues
            if (geocodeCache.current.size > 50) {
              const firstKey = geocodeCache.current.keys().next().value;
              geocodeCache.current.delete(firstKey);
            }

            updateLocation(
              { ...location, address: place.formatted_address },
              place
            );
          } else if (status === "OVER_QUERY_LIMIT") {
            if (retryCount < 3) {
              // Exponential backoff: wait 1s, 2s, 4s
              const delay = Math.pow(2, retryCount) * 1000;
              setTimeout(() => {
                attemptGeocode(retryCount + 1);
              }, delay);
            } else {
              message.warning(
                "Location lookup temporarily unavailable due to high usage. Please try again in a few minutes."
              );
            }
          } else if (status === "REQUEST_DENIED") {
            message.error("Location services are not available. Please check your API key configuration.");
          } else {
            message.warning("Unable to determine address for this location.");
          }
        });
      };

      attemptGeocode();
    }, 500), // Increased debounce to 500ms
    [updateLocation]
  );

  // Function to get current location using browser geolocation
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      message.error("Geolocation is not supported by this browser.");
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          address: "",
        };

        updateLocation(location);
        reverseGeocode(location);
        message.success("Current location set successfully!");
        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        let errorMessage = "Unable to get current location. ";

        switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += "Location access denied by user.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += "Location information is unavailable.";
          break;
        case error.TIMEOUT:
          errorMessage += "Location request timed out.";
          break;
        default:
          errorMessage += "An unknown error occurred.";
          break;
        }

        message.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, [updateLocation, reverseGeocode]);

  // Function to fetch delivery partner data and prefill form
  const fetchDeliveryPartnerData = useCallback(async () => {
    try {
      setLoading(true);
      const userEmail = getUserEmail();

      if (!userEmail) {
        message.error("User email not found. Please login again.");
        return;
      }

      const response = await API.Api(
        DELIVERY_PARTNER_LIST + `?email=${encodeURIComponent(userEmail)}`
      ).getWithNoToken();

      if (response?.status && response?.response?.data?.data?.length > 0) {
        const dpData = response.response.data.data[0];

        // Store delivery partner data for later use in submission
        setDeliveryPartnerData(dpData);

        // Prefill form fields with API data
        form.setFieldsValue({
          address: dpData.address || "",
          city: dpData.city || "",
          state: dpData.state || "",
          pincode: dpData.pincode || "",
        });

        // Update search input with address
        if (dpData.address) {
          setSearchValue(dpData.address);
          form.setFieldValue("searchLocation", dpData.address);
          // Clear suggestions status to prevent showing "Searching..." for prefilled data
          setSuggestions([]);
          setSuggestionsStatus("");
        }

        message.success("Delivery partner data loaded successfully!");
      } else {
        message.info("No delivery partner data found for this email.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Failed to load delivery partner data."
      );
    } finally {
      setLoading(false);
    }
  }, [getUserEmail, form]);

  useEffect(() => {
    // Fetch delivery partner data on component mount
    fetchDeliveryPartnerData();
  }, [fetchDeliveryPartnerData]);

  useEffect(() => {
    // Load Google Maps script only once
    if (!window.google && !window.googleMapsLoading) {
      window.googleMapsLoading = true; // Prevent multiple script loads

      const currentEnv = process.env.REACT_APP_ENV;
      let apiKey;

      switch (currentEnv) {
      case "prod":
        apiKey = "AIzaSyCi9KgjhJBmM60wIf0xF1FqRAEGfKTTZOY"; // Production key
        break;
      case "qa":
        apiKey = "AIzaSyA3AABkjCpa5pjazzP6JxK4YuHmzDGRFWs"; // QA key
        break;
      case "dev":
        apiKey = "AIzaSyCQT7-KBNzhP3qT7X_Tg2qFE8d9WiXCNQc"; // Development key
        break;
      default:
        // Fallback
        apiKey = "AIzaSyA3AABkjCpa5pjazzP6JxK4YuHmzDGRFWs"; // QA key
        break;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Create a global callback function
      window.initGoogleMaps = () => {
        window.googleMapsLoading = false;
        setGoogleMapsLoaded(true); // Set state when Google Maps is loaded
        initializeMap();
      };

      script.onerror = () => {
        window.googleMapsLoading = false;
        message.error(
          "Failed to load Google Maps. Please check your internet connection and try refreshing the page."
        );
      };

      document.head.appendChild(script);
    } else if (
      window.google &&
      window.google.maps &&
      window.google.maps.places
    ) {
      setGoogleMapsLoaded(true); // Set state if already loaded
      initializeMap();
    }
  }, []);

  const initializeMap = () => {
    if (!mapRef.current) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: selectedLocation,
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
    });

    const markerInstance = new window.google.maps.Marker({
      position: selectedLocation,
      map: mapInstance,
      draggable: true,
      title: "COD Center Location",
    });

    // Debounced function for handling location updates
    const debouncedLocationUpdate = debounce((location) => {
      updateLocation(location);
      reverseGeocode(location);
    }, 500);

    // Add click listener to map with debouncing
    mapInstance.addListener("click", (event) => {
      const location = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
        address: "",
      };
      debouncedLocationUpdate(location);
    });

    // Add drag listener to marker with debouncing
    markerInstance.addListener("dragend", (event) => {
      const location = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
        address: "",
      };
      debouncedLocationUpdate(location);
    });

    setMap(mapInstance);
    setMarker(markerInstance);
  };

  const handleSubmit = (values) => {
    // Store form values and show confirmation modal
    setFormValues(values);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setLoading(true);
      setShowConfirmModal(false);

      // Check if location has been properly selected
      if (!locationSelected) {
        message.error(
          "Please select a location from the dropdown or use current location"
        );
        setLoading(false);
        return;
      }

      if (!deliveryPartnerData) {
        message.error(
          "Delivery partner data not found. Please refresh the page."
        );
        setLoading(false);
        return;
      }

      if (!formValues) {
        message.error("Form data not found. Please try again.");
        setLoading(false);
        return;
      }

      // Prepare the payload according to the API specification
      const payload = {
        dpId: deliveryPartnerData.dpId,
        email: deliveryPartnerData.email,
        centerName: deliveryPartnerData.dpName,
        otpVerificationNumber: deliveryPartnerData.contactPersonPhone,
        contactName: deliveryPartnerData.contactPersonName,
        contactNumber: deliveryPartnerData.contactPersonPhone,
        centerOpenTime: {
          label: "6:30 AM",
          value: {
            hours: 6,
            minutes: 30,
            totalMin: 390,
          },
          isDisabled: false,
        },
        centerCloseTime: {
          label: "11:00 PM",
          value: {
            hours: 11,
            minutes: 0,
            totalMin: 1380,
          },
          isDisabled: false,
        },
        cashLimit: 1000000,
        codCenterCashDepositDay: 45,
        address: {
          address1: formValues.address || "",
          address2: "",
          zip: formValues.pincode.toString() || "",
          state: formValues.state || "",
          city: formValues.city || "",
          geoLocation: {
            type: "Point",
            coordinates: [
              selectedLocation.lng, // longitude first in GeoJSON format
              selectedLocation.lat, // latitude second
            ],
          },
        },
        insertedOn: new Date().toISOString(),
        isActive: true,
        isDefaulter: false,
        isCashLimitReached: false,
      };

      // Make the API call
      const response = await API.Api(CREATE_COD_CENTER).post(payload);

      if (response?.status) {
        message.success(
          "COD Center onboarding request submitted successfully!"
        );
        form.resetFields();
        navigate("/profile");
      } else {
        const errorMessage =
          response?.response?.data?.message || "Failed to create COD Center";
        message.error(errorMessage);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Failed to create COD Center. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setFormValues(null);
  };

  const handleCancel = () => {
    form.resetFields();
    setLocationSelected(false); // Reset location selection state
    setSelectedLocation({
      lat: 18.5204, // Reset to default Pune location
      lng: 73.8567,
      address: "",
    });
    navigate("/profile");
  };

  // Initialize Google Places services when Maps is loaded
  useEffect(() => {
    if (googleMapsLoaded && window.google && window.google.maps && window.google.maps.places) {
      const autoService = new window.google.maps.places.AutocompleteService();
      setAutocompleteService(autoService);
      
      // Create a dummy map element for PlacesService (required by Google)
      const dummyMap = new window.google.maps.Map(document.createElement("div"));
      const placeService = new window.google.maps.places.PlacesService(dummyMap);
      setPlacesService(placeService);
    }
  }, [googleMapsLoaded]);

  // Function to search places using direct API
  const searchPlaces = useCallback((input) => {
    if (!autocompleteService || !input || input.length < 2) {
      setSuggestions([]);
      setSuggestionsStatus("");
      return;
    }

    // Set searching status immediately
    setSuggestionsStatus("SEARCHING");

    const request = {
      input: input,
      componentRestrictions: { country: "IN" },
      types: ["establishment", "geocode"] // Include both places and addresses
    };

    // Retry function with exponential backoff
    const attemptSearch = (retryCount = 0) => {
      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setSuggestionsStatus("OK");
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
          setSuggestionsStatus("ZERO_RESULTS");
        } else if (status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
          if (retryCount < 2) {
            // Keep searching status during retry
            // Exponential backoff: wait 2s, 4s
            const delay = Math.pow(2, retryCount + 1) * 1000;
            setTimeout(() => {
              attemptSearch(retryCount + 1);
            }, delay);
          } else {
            setSuggestions([]);
            setSuggestionsStatus("ERROR");
            message.warning("Search limit reached. Please wait a moment and try again.");
          }
        } else {
          setSuggestions([]);
          setSuggestionsStatus("ERROR");
          
          // Show user-friendly error messages
          if (status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
            message.error("Places search is not available. Please check your API key configuration.");
          } else {
            message.error("Search failed. Please try again.");
          }
        }
      });
    };

    attemptSearch();
  }, [autocompleteService]);

  // Function to get place details
  const getPlaceDetails = useCallback((placeId, description) => {
    if (!placesService) {
      return;
    }

    const request = {
      placeId: placeId,
      fields: ["geometry", "formatted_address", "address_components", "name"]
    };

    placesService.getDetails(request, (place, status) => {
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address: place.formatted_address || description,
        };

        updateLocation(location, place);
        setSearchValue(description);
        form.setFieldValue("searchLocation", description);
        
        message.success(`Location selected: ${description}`);
      } else {
        message.error("Failed to get location details");
      }
    });
  }, [placesService, updateLocation, form]);

  // useEffect to handle search when searchValue changes
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Only trigger search if value is long enough and not empty
    if (searchValue && searchValue.length > 2) {
      // Set status to indicate search will start
      setSuggestionsStatus("PREPARING");
      // Increased debounce to 1000ms to reduce API calls
      searchTimeoutRef.current = setTimeout(() => {
        searchPlaces(searchValue);
      }, 1000);
    } else {
      // Clear suggestions for short input
      setSuggestions([]);
      setSuggestionsStatus("");
    }
  }, [searchValue, searchPlaces]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion) => {
    getPlaceDetails(suggestion.place_id, suggestion.description);
  }, [getPlaceDetails]);

  return (
    <div className="cod-center-onboarding-container">
      <p className="pl-2 pr-2 pt-1 profile-heading">
        <Heading title={"Back"} navigateTo={"/profile"} />
      </p>
      <Spin spinning={loading}>
        <div className="master-main-container m-1 p-1">
          <div className="page-header">
            <h2>Onboard Yourself as COD Center</h2>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="global-form cod-center-form"
            scrollToFirstError={{
              behavior: "smooth",
              block: "center",
              inline: "center",
            }}
          >
            <Row gutter={24}>
              {/* Center Address Section */}
              <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                <div className="center-address-section">
                  <h3>Center Address</h3>
                  <p>
                    In order to correctly capture your location, follow below
                    two steps:
                  </p>
                  <ol>
                    <li>
                      In below search box type your society name followed by
                      area name and city (ex. Harmony Pearl, Sola, Ahmedabad).
                      Select the correct address from search drop box
                    </li>
                    <li>
                      Now to precisely locate your building, drag the marker
                      from the map and drop it on your building or society gate.
                      If required, you can zoom in/zoom out by clicking on + & -
                      buttons on map located at bottom right corner
                    </li>
                  </ol>
                </div>
              </Col>

              {/* Map and Form Fields Row */}
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className="map-search-container">
                  <div ref={mapRef} className="google-map" />
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <div className="google-places-search-container">
                      <Form.Item
                        name="searchLocation"
                        label="🔍 Search Location"
                        rules={[
                          {
                            required: true,
                            message: "Please search and select a location",
                          },
                          {
                            validator: () => {
                              if (locationSelected) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error(
                                  "Please select a location from the dropdown or use current location"
                                )
                              );
                            },
                          },
                        ]}
                      >
                        <div className="places-container">
                          <div className="places-input-group">
                            <div className="places-input-wrapper">
                              <Combobox onSelect={(value) => {
                                const selectedSuggestion = suggestions.find(s => s.description === value);
                                if (selectedSuggestion) {
                                  handleSuggestionSelect(selectedSuggestion);
                                }
                              }}>
                                <ComboboxInput
                                  className="combobox-input"
                                  value={searchValue}
                                  onChange={(e) => {
                                    setSearchValue(e.target.value);
                                    form.setFieldValue("searchLocation", e.target.value);
                                  }}
                                  placeholder="Type to search places (e.g., Harmony Pearl, Sola, Ahmedabad)"
                                />
                                <ComboboxPopover className="combobox-popover">
                                  <ComboboxList>
                                    {(() => {
                                      if (!googleMapsLoaded) {
                                        return (
                                          <div className="combobox-option loading">
                                            Loading Google Maps...
                                          </div>
                                        );
                                      } else if (searchValue.length > 2) {
                                        if (suggestionsStatus === "OK" && suggestions && suggestions.length > 0) {
                                          return suggestions.map((suggestion) => (
                                            <ComboboxOption
                                              key={suggestion.place_id}
                                              value={suggestion.description}
                                              className="combobox-option"
                                            />
                                          ));
                                        } else if (suggestionsStatus === "ZERO_RESULTS") {
                                          return (
                                            <div className="combobox-option no-results">
                                              No results found
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <div className="combobox-option searching">
                                              Searching...
                                            </div>
                                          );
                                        }
                                      } else {
                                        return null;
                                      }
                                    })()}
                                  </ComboboxList>
                                </ComboboxPopover>
                              </Combobox>
                            </div>
                            <Button
                              type="default"
                              onClick={getCurrentLocation}
                              loading={gettingLocation}
                              className="location-button"
                              title="Use your current location"
                            >
                              📍 Current Location
                            </Button>
                          </div>
                        </div>
                      </Form.Item>
                      <div className="search-hint-text">
                        Start typing to see location suggestions or click
                        &quot;Current Location&quot; to use your GPS location
                      </div>
                    </div>
                    <Form.Item
                      name="address"
                      label="Address"
                      rules={[
                        {
                          required: true,
                          message: "Please enter address",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Enter address"
                        maxLength={200}
                        className="address-textarea"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      name="pincode"
                      label="Pincode *"
                      rules={[
                        {
                          required: true,
                          message: "Please enter pincode",
                        },
                        {
                          pattern: /^[1-9][0-9]{5}$/,
                          message: "Please enter a valid 6-digit pincode",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter 6-digit pincode"
                        maxLength={6}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      name="state"
                      label="State *"
                      rules={[
                        {
                          required: true,
                          message: "Please enter state name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter state name"
                        maxLength={50}
                        onKeyPress={(e) => {
                          if (/[^A-Za-z ]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      name="city"
                      label="City *"
                      rules={[
                        {
                          required: true,
                          message: "Please enter city name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter city name"
                        maxLength={50}
                        onKeyPress={(e) => {
                          if (/[^A-Za-z ]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  {/* Display Latitude and Longitude */}
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <div className="location-coordinates">
                      <strong>Latitude:</strong>{" "}
                      {selectedLocation.lat.toFixed(5)}{" "}
                      <strong>Longitude:</strong>{" "}
                      {selectedLocation.lng.toFixed(5)}
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Hidden fields for latitude and longitude */}
              <Form.Item name="latitude" className="hidden-field">
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="longitude" className="hidden-field">
                <Input type="hidden" />
              </Form.Item>
            </Row>

            <div className="form-actions">
              <Button
                type="default"
                onClick={handleCancel}
                className="cancel-btn"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="submit-btn"
                loading={loading}
              >
                Submit Onboarding Request
              </Button>
            </div>
          </Form>
        </div>
      </Spin>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm COD Center Onboarding"
        open={showConfirmModal}
        onOk={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
        okText="Submit"
        cancelText="Cancel"
        confirmLoading={loading}
        width={600}
        centered
      >
        <div className="modal-content">
          <p className="modal-description">
            COD Center will be created with the below details.
          </p>

          {formValues && (
            <div className="modal-details">
              <div className="detail-item">
                <strong>Center Name:</strong>{" "}
                {deliveryPartnerData?.dpName || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Contact Person:</strong>{" "}
                {deliveryPartnerData?.contactPersonName || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Contact Number:</strong>{" "}
                {deliveryPartnerData?.contactPersonPhone || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Email:</strong> {deliveryPartnerData?.email || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Address:</strong> {formValues.address || "N/A"}
              </div>
              <div className="detail-item">
                <strong>City:</strong> {formValues.city || "N/A"}
              </div>
              <div className="detail-item">
                <strong>State:</strong> {formValues.state || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Pincode:</strong> {formValues.pincode || "N/A"}
              </div>
              <div className="detail-item">
                <strong>Location:</strong> {selectedLocation.lat.toFixed(5)},{" "}
                {selectedLocation.lng.toFixed(5)}
              </div>
              <div className="detail-item">
                <strong>Operating Hours:</strong> 6:30 AM - 11:00 PM
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CODCenterOnboarding;
